import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    Popper, 
    Paper, 
    ClickAwayListener, 
    Typography, 
    Button,
    TextField,
    Divider,
    IconButton,
    Select,
    MenuItem,
    SelectChangeEvent,
    FormControlLabel,
    Checkbox,
    CircularProgress,
    FormControl,
    InputLabel,
    Box,
    Autocomplete
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { WorkflowNode } from '../../types/workflow';
import { NodeConfig } from '../../types/nodes';
import { useGetAvailableActivities, useUpdateActivityNodeAuthToken } from '../../api/activityNodeApi';
import { textFieldSX } from '../../constants/textFieldCSS';
import { useGetServiceAuthTokensForService, ServiceAuthToken } from '../../api/serviceAuthTokenApi';
import axios from 'axios';
import { toast } from "react-toastify";
import customFetch from "../../utils/customFetch";

interface TokenOption {
    id: string;
    type: 'service' | 'bot';
    serviceUniqueId: string;
    serviceReadableInfo: string;
    label: string;
}

export interface AgentTool {
    id: string;
    name: string;
    service: string;
    action: string;
    config: Record<string, any>;
    service_auth_token_id?: string;
}

interface ConfigField {
    name: string;
    label: string;
    type: string;
    required: boolean;
    placeholder?: string;
    display_order: number;
    options_url?: string;
    depends_on?: string;
    default?: any;
}

interface ToolConfigurationPopperProps {
    open: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    onSave: (toolId: string, config: Record<string, any>) => void;
    tool: AgentTool;
}

const ToolConfigurationPopper: React.FC<ToolConfigurationPopperProps> = ({
    open,
    anchorEl,
    onClose,
    onSave,
    tool
}) => {
    const [config, setConfig] = useState<Record<string, any>>(tool.config || {});
    const { data: activities } = useGetAvailableActivities();
    const [configFields, setConfigFields] = useState<ConfigField[]>([]);
    const { data: serviceAuthTokens } = useGetServiceAuthTokensForService(tool.service);
    const updateAuthToken = useUpdateActivityNodeAuthToken();
    const [currentToken, setCurrentToken] = useState<TokenOption | null>(null);
    const [fieldOptions, setFieldOptions] = useState<Record<string, { id: string, label: string }[]>>({});
    const [loadingOptions, setLoadingOptions] = useState<Record<string, boolean>>({});

    console.log({
        open,
    anchorEl,
    onClose,
    onSave,
    tool
    });

    // First, let's memoize the config processing logic
    const processConfigFields = useCallback((activity: any, currentConfig: Record<string, any>) => {
        if (!activity) return [];
        
        const metadata = activity.metadata || {};
        
        // Process config fields directly from metadata
        if (metadata.config_fields) {
            const fieldsArray = Object.entries(metadata.config_fields).map(([name, fieldData]) => ({
                name,
                ...(fieldData as any),
            }));
            
            fieldsArray.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
            return fieldsArray;
        }
        
        // Fall back to actions approach
        let actions = metadata.actions;
        if (!actions) {
            if (metadata[tool.action]) {
                actions = { [tool.action]: metadata[tool.action] };
            } else {
                actions = metadata;
            }
        }
        
        const actionKey = Object.keys(actions).find(key => 
            key === tool.action || key.includes(tool.action) || tool.action.includes(key)
        );
        
        if (actionKey && actions[actionKey]) {
            const actionData = actions[actionKey];
            const actionMetadata = actionData.metadata || actionData;
            const configFieldsData = actionMetadata.config_fields || {};
            
            const fieldsArray = Object.entries(configFieldsData).map(([name, fieldData]) => ({
                name,
                ...(fieldData as any),
            }));
            
            fieldsArray.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
            return fieldsArray;
        }
        
        return [];
    }, [tool.action]);

    // Memoize the activity finding logic
    const findMatchingActivity = useCallback((activities: any[]) => {
        return activities?.find(activity => 
            activity.name === tool.service || 
            (activity.metadata && activity.metadata.service === tool.service)
        );
    }, [tool.service]);

    // Update the main useEffect to use memoized functions
    useEffect(() => {
        if (!activities || !tool.service || !tool.action) return;
        
        const activity = findMatchingActivity(activities);
        if (!activity) {
            console.warn(`Activity not found for service: ${tool.service}`);
            return;
        }
        
        const fields = processConfigFields(activity, config);
        setConfigFields(fields);
        
        // Initialize config with default values in a single batch
        const initialConfig = { ...config };
        let hasChanges = false;
        
        fields.forEach(field => {
            if (initialConfig[field.name] === undefined && field.default !== undefined) {
                initialConfig[field.name] = field.default;
                hasChanges = true;
            }
        });
        
        if (hasChanges) {
            setConfig(initialConfig);
        }
        
        // Load initial options for non-dependent select fields
        const selectFields = fields.filter(field => 
            field.type === 'select' && 
            field.options_url && 
            !field.depends_on
        );
        
        selectFields.forEach(field => {
            loadFieldOptions(field.name, field.options_url!);
        });
    }, [activities, tool.service, tool.action, findMatchingActivity, processConfigFields]);

    // Optimize loadFieldOptions to prevent unnecessary state updates
    const loadFieldOptions = useCallback(async (fieldName: string, optionsUrl: string) => {
        // Skip if already loading or if options exist
        if (loadingOptions[fieldName] || fieldOptions[fieldName]) return;
        
        // Replace placeholders in the URL with actual values
        // Add support for {node_id} replacement with tool.id
        const processedUrl = optionsUrl.replace(/\{([^}]+)\}/g, (_, param) => {
            if (param === 'node_id') return tool.id;
            return config[param] || '';
        });
        
        if (processedUrl.includes('{') && processedUrl.includes('}')) return;
        
        setLoadingOptions(prev => ({ ...prev, [fieldName]: true }));
        
        try {
            const response = await customFetch(processedUrl)
                .then(async(res) => {
                    const data = await res.json();
                    return res.ok
                        ? data
                        : Promise.reject(new Error(`Failed to fetch options for ${fieldName}`))
                });
            
            if (response && Array.isArray(response)) {
                setFieldOptions(prev => {
                    // Skip update if options already exist
                    if (prev[fieldName]) return prev;
                    
                    return {
                        ...prev,
                        [fieldName]: response.map((item: any) => ({
                            id: item.id || item.value || item,
                            label: item.name || item.label || item.toString()
                        }))
                    };
                });
            }
        } catch (error) {
            console.error(`Error loading options for ${fieldName}:`, error);
            toast.error(`Failed to load options for ${fieldName}`);
        } finally {
            setLoadingOptions(prev => ({ ...prev, [fieldName]: false }));
        }
    }, [config, fieldOptions, loadingOptions, tool.id]);

    // Optimize dependent fields effect
    useEffect(() => {
        const dependentFields = configFields.filter(field => 
            field.type === 'select' && 
            field.options_url && 
            field.depends_on && 
            config[field.depends_on] &&
            !fieldOptions[field.name]
        );
        
        dependentFields.forEach(field => {
            loadFieldOptions(field.name, field.options_url!);
        });
    }, [configFields, config, loadFieldOptions]);

    // Memoize handleConfigChange to prevent unnecessary re-renders
    const handleConfigChange = useCallback((field: string, value: any) => {
        setConfig(prev => {
            const next = { ...prev, [field]: value };
            
            // Handle dependent fields
            const dependentFields = configFields.filter(f => f.depends_on === field);
            dependentFields.forEach(depField => {
                next[depField.name] = '';
            });
            
            return next;
        });
    }, [configFields]);

    // Memoize token options transformation
    const tokenOptions = useMemo(() => {
        if (!serviceAuthTokens) return [];
        return serviceAuthTokens.map((token: ServiceAuthToken) => ({
            id: token.id,
            type: 'service' as const,
            serviceUniqueId: token.service_unique_id,
            serviceReadableInfo: token.service_readable_info,
            label: token.token_name || token.service_readable_info
        }));
    }, [serviceAuthTokens]);

    // Set initial token
    useEffect(() => {
        if (tool?.service_auth_token_id && tokenOptions.length > 0) {
            const token = tokenOptions.find((t: TokenOption) => t.id === tool.service_auth_token_id);
            if (token) {
                setCurrentToken(token);
            }
        }
    }, [tokenOptions, tool]);

    // Render a config field with improved loading state handling
    const renderConfigField = useCallback((field: ConfigField) => {
        const isLoading = loadingOptions[field.name] || false;
        const options = fieldOptions[field.name] || [];
        const hasDependency = !!field.depends_on;
        const dependencyValue = field.depends_on ? config[field.depends_on] : null;
        const dependencyFilled = hasDependency ? !!dependencyValue : true;
        
        switch (field.type) {
            case 'select':
                return (
                    <FormControl 
                        key={field.name} 
                        fullWidth 
                        variant="outlined" 
                        margin="dense"
                        required={field.required === true}
                        disabled={!dependencyFilled}
                    >
                        <InputLabel>{field.label}</InputLabel>
                        <Select
                            value={config[field.name] || ''}
                            onChange={(e) => handleConfigChange(field.name, e.target.value)}
                            label={field.label}
                            disabled={isLoading || !dependencyFilled}
                            MenuProps={{
                                style: { maxHeight: 300 }
                            }}
                        >
                            {isLoading ? (
                                <MenuItem value="" disabled>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <CircularProgress size={20} sx={{ mr: 1 }} />
                                        Loading...
                                    </Box>
                                </MenuItem>
                            ) : options.length === 0 ? (
                                <MenuItem value="" disabled>No options available</MenuItem>
                            ) : (
                                options.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        {option.label}
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                        {isLoading && (
                            <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, mt: 0.5 }}>
                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                <Typography variant="caption" color="text.secondary">
                                    Loading options...
                                </Typography>
                            </Box>
                        )}
                    </FormControl>
                );
                
            case 'checkbox':
                const isChecked = config[field.name] === true;
                
                return (
                    <div key={field.name} className="mb-3">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isChecked}
                                    onChange={(e) => handleConfigChange(field.name, e.target.checked)}
                                    size="small"
                                />
                            }
                            label={
                                <Typography variant="subtitle2">
                                    {field.label} {field.required && <span className="text-red-500">*</span>}
                                </Typography>
                            }
                        />
                    </div>
                );
                
            default:
                return (
                    <div key={field.name} className="mb-3">
                        <Typography variant="subtitle2" className="mb-1">
                            {field.label} {field.required && <span className="text-red-500">*</span>}
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            value={config[field.name] || ''}
                            onChange={(e) => handleConfigChange(field.name, e.target.value)}
                            placeholder={field.placeholder || ''}
                            sx={textFieldSX}
                            required={field.required}
                        />
                    </div>
                );
        }
    }, [config, fieldOptions, loadingOptions, handleConfigChange]);

    // Then define renderedConfigFields
    const renderedConfigFields = useMemo(() => {
        if (configFields.length === 0) {
            return (
                <Typography variant="body2" sx={{ color: 'text.secondary', py: 2, textAlign: 'center' }}>
                    This tool doesn't require any configuration.
                </Typography>
            );
        }
        
        return configFields.map(field => renderConfigField(field));
    }, [configFields, renderConfigField]);

    const handleTokenChange = (token: TokenOption | null) => {
        setCurrentToken(token);
        if (token && tool.id) {
            updateAuthToken.mutate({
                nodeId: tool.id,
                serviceAuthTokenId: token.id
            });
        }
    };

    const handleSave = () => {
        onSave(tool.id, config);
    };

    return (
        <Popper
            open={open}
            anchorEl={anchorEl}
            placement="right-start"
            sx={{ zIndex: 1300, width: 400, maxWidth: '90vw' }}
        >
            <Paper elevation={3} sx={{ p: 2, maxHeight: '80vh', overflow: 'auto' }}>
                <div className="flex justify-between items-center mb-4">
                    <Typography variant="h6">Tool Configuration</Typography>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </div>
                
                {/* Auth Token Selection */}
                {tokenOptions.length > 0 && (
                    <div className="mb-4">
                        <Typography variant="subtitle2" className="mb-1">Authentication Token</Typography>
                        <Autocomplete
                            value={currentToken}
                            onChange={(_, newValue) => handleTokenChange(newValue)}
                            options={tokenOptions}
                            getOptionLabel={(option) => option.label}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Select authentication token"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    <div>
                                        <Typography variant="body2">{option.label}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {option.serviceReadableInfo}
                                        </Typography>
                                    </div>
                                </li>
                            )}
                        />
                    </div>
                )}
                
                {/* Configuration Fields */}
                <div>
                    {renderedConfigFields}
                </div>
                
                <div className="mt-4 flex justify-end">
                    <Button 
                        variant="outlined" 
                        color="primary" 
                        onClick={handleSave}
                    >
                        Save Configuration
                    </Button>
                </div>
            </Paper>
        </Popper>
    );
};

export default ToolConfigurationPopper; 